import api from './api';

export interface McpApiKey {
  id: string;
  name: string;
  prefix: string;
  status: 'active' | 'revoked';
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateMcpApiKeyResponse {
  api_key: McpApiKey;
  key: string;
}

const ACTIVE_KEY_STORAGE_KEY = 'mcp_active_api_key';

export const mcpService = {
  getKeys: async () => {
    const response = await api.get<McpApiKey[]>('/mcp/keys');
    return response.data;
  },

  createKey: async (name: string) => {
    const response = await api.post<CreateMcpApiKeyResponse>('/mcp/keys', { name });
    return response.data;
  },

  revokeKey: async (id: string) => {
    await api.delete(`/mcp/keys/${id}`);
  },

  /**
   * Stores the user's active MCP API key in localStorage.
   * This key will be sent with chat requests to the MCP server
   * so the AI assistant operates under the user's identity.
   */
  setActiveKey: (key: string) => {
    localStorage.setItem(ACTIVE_KEY_STORAGE_KEY, key);
  },

  /**
   * Retrieves the currently active MCP API key from localStorage.
   * Returns null if no key has been set.
   */
  getActiveKey: (): string | null => {
    return localStorage.getItem(ACTIVE_KEY_STORAGE_KEY);
  },

  /**
   * Clears the active MCP API key from localStorage.
   * The AI assistant will stop being able to make authenticated requests.
   */
  clearActiveKey: () => {
    localStorage.removeItem(ACTIVE_KEY_STORAGE_KEY);
  },

  /**
   * Returns whether an active key is currently set.
   */
  hasActiveKey: (): boolean => {
    const key = localStorage.getItem(ACTIVE_KEY_STORAGE_KEY);
    return !!key && key.trim().length > 0;
  },
};
